import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { difference, isEmpty, keys, omit, set } from 'lodash';
import { CanadaProvinces } from 'models/enums/CanadaProvinces';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import productCloneApi from 'models/productManagement/product/clone/Api.Product.Clone';
import productDeleteApi from 'models/productManagement/product/delete/Api.Product.Delete';
import Product from 'models/productManagement/product/Model.Product';
import productSaveApi from 'models/productManagement/product/save/Api.Product.Save';
import productDocumentDeleteApi from 'models/productManagement/productDocument/delete/Api.ProductDocument.Delete';
import productDocumentOrderApi from 'models/productManagement/productDocument/order/Api.ProductDocument.Order';
import productDocumentReplaceApi from 'models/productManagement/productDocument/replace/Api.ProductDocument.Replace';
import productDocumentSaveApi from 'models/productManagement/productDocument/save/Api.ProductDocument.Save';
import productDocumentUpdateApi from 'models/productManagement/productDocument/update/Api.ProductDocument.Update';
import productPricingAutomationRuleDeleteApi from 'models/productManagement/productPricingAutomationRule/delete/Api.ProductPricingAutomationRule.Delete';
import ProductPricingAutomationRule from 'models/productManagement/productPricingAutomationRule/Model.ProductPricingAutomationRule';
import productPricingAutomationRuleSaveApi from 'models/productManagement/productPricingAutomationRule/save/Api.ProductPricingAutomationRule.Save';
import productRefundDocumentDeleteApi from 'models/productManagement/productRefundDocument/delete/Api.ProductRefundDocument.Delete';
import productRefundDocumentOrderApi from 'models/productManagement/productRefundDocument/order/Api.ProductRefundDocument.Order';
import productRefundDocumentReplaceApi from 'models/productManagement/productRefundDocument/replace/Api.ProductRefundDocument.Replace';
import productRefundDocumentSaveApi from 'models/productManagement/productRefundDocument/save/Api.ProductRefundDocument.Save';
import productRefundDocumentUpdateApi from 'models/productManagement/productRefundDocument/update/Api.ProductRefundDocument.Update';
import productTransferOwnershipDocumentDeleteApi from 'models/productManagement/productTransferOwnershipDocument/delete/Api.ProductTransferOwnershipDocument.Delete';
import productTransferOwnershipDocumentOrderApi from 'models/productManagement/productTransferOwnershipDocument/order/Api.ProductTransferOwnershipDocument.Order';
import productTransferOwnershipDocumentReplaceApi from 'models/productManagement/productTransferOwnershipDocument/replace/Api.ProductTransferOwnershipDocument.Replace';
import productTransferOwnershipDocumentSaveApi from 'models/productManagement/productTransferOwnershipDocument/save/Api.ProductTransferOwnershipDocument.Save';
import productTransferOwnershipDocumentUpdateApi from 'models/productManagement/productTransferOwnershipDocument/update/Api.ProductTransferOwnershipDocument.Update';
import {
  hasIncompleteAutomationEffects,
  hasIncompleteAutomationExpressions,
} from 'module/automation/helpers/ruleHelpers';
import { createPricingAutomationSchema } from 'module/automation/schemas/PricingAutomationSchema';
import { useEffect, useMemo, useState } from 'react';
import { productIndexRoute } from './Product.Index';

export enum ProductAction {
  EDIT = 'edit',
  DELETE = 'delete',
  CLONE = 'clone',
}
export enum ProductEditTab {
  INFO = 'info',
  DOCUMENTS = 'documents',
  REFUND_DOCUMENTS = 'refundDocuments',
  TRANSFER_OWNERSHIP_DOCUMENTS = 'transferOwnershipDocuments',
  DOC_TEMPLATES = 'docTemplates',
  TAXES = 'taxes',
  PROVINCE_GROUPS = 'provinceGroups',
  PRICING_AUTOMATION_RULES = 'pricingAutomationRules',
  REPORT_SETTINGS = 'reportSettings',
}
export type VerifyProductAction = Utils.VerifyExtends<
  Module.ProductManagement.Product.Actions,
  Utils.ValueOf<typeof ProductAction>
>;
export type VerifyProductEditTab = Utils.VerifyExtends<
  Module.ProductManagement.Product.EditTab,
  Utils.ValueOf<typeof ProductEditTab>
>;
export type ProductForm = Utils.FormData<
  Model.IProduct & {
    productDocuments?: Array<Utils.FormData<Model.IProductDocument> & { isFileReplaced?: boolean }>;
    productRefundDocuments?: Array<Utils.FormData<Model.IProductRefundDocument> & { isFileReplaced?: boolean }>;
    productTransferOwnershipDocuments?: Array<
      Utils.FormData<Model.IProductTransferOwnershipDocument> & { isFileReplaced?: boolean }
    >;
    productPricingAutomationRules?: Array<
      Utils.FormData<Model.IProductPricingAutomationRule> & { isDeleted?: boolean }
    >;
  }
>;
export default function useProduct() {
  const { permissions, user } = useAuthStore();

  const { lang } = getLocales();
  const pageRouter = usePageRouter<Module.ProductManagement.Product.Params, Module.ProductManagement.Product.Query>({
    route: productIndexRoute,
  });
  const productId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;
  const [isLoading, setIsLoading] = useState(false);
  const canView =
    permissions.CREATE_PRODUCT ||
    permissions.EDIT_PRODUCT ||
    permissions.DELETE_PRODUCT ||
    permissions.EDIT_PRODUCTTAX ||
    permissions.VIEW_CUSTOMPRICEPOINT ||
    permissions.CREATE_CUSTOMPRICEPOINT ||
    permissions.EDIT_CUSTOMPRICEPOINT ||
    permissions.DELETE_CUSTOMPRICEPOINT ||
    permissions.VIEW_NONWARRANTYPRICEPOINT ||
    permissions.CREATE_NONWARRANTYPRICEPOINT ||
    permissions.EDIT_NONWARRANTYPRICEPOINT ||
    permissions.DELETE_NONWARRANTYPRICEPOINT ||
    permissions.VIEW_PRODUCTTIERPRICEPOINT ||
    permissions.CREATE_PRODUCTTIERPRICEPOINT ||
    permissions.EDIT_PRODUCTTIERPRICEPOINT ||
    permissions.DELETE_PRODUCTTIERPRICEPOINT ||
    permissions.VIEW_PRODUCTPRICINGPLAN ||
    permissions.CREATE_PRODUCTPRICINGPLAN ||
    permissions.EDIT_PRODUCTPRICINGPLAN ||
    permissions.DELETE_PRODUCTPRICINGPLAN;
  const listApi = useApi({
    action: Product.list,
    default: {
      limit: defaultLimit,
      offset: defaultOffset,
      dealerId: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ?? undefined,
    },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({ ...pageRouter.params }, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const {
    payload: product,
    execute: reloadProduct,
    isExecuting: isEditLoading,
    reset: resetProduct,
  } = useApi(
    {
      action: Product.find,
      body: { id: productId },
      default: null,
      wait: !productId,
      callback: (req) => productPricingAutomationRuleListApi.execute({ productId: [req.id] }),
    },
    [productId]
  );
  useEffect(() => {
    if (!productId) resetProduct(true);
  }, [productId]);

  const deleteApi = useApi({
    action: productDeleteApi,
    callback: () => {
      listApi.execute((body) => body);
      pageRouter.redirect({ ...pageRouter.params, action: undefined }, { ...pageRouter.query, editTab: undefined });
    },
    wait: true,
  });

  const cloneApi = useApi({
    action: productCloneApi,
    callback: (_, res) => {
      pageRouter.redirect(
        { id: res?.payload?.toString(), action: ProductAction.EDIT },
        { ...pageRouter.query, editTab: ProductEditTab.INFO }
      );
    },
    wait: true,
  });

  const currentProduct = product?.toJSON();
  //#region Product pricing automation rules
  const productPricingAutomationRuleListApi = useApi(
    {
      action: ProductPricingAutomationRule.list,
      default: {
        productId: [currentProduct?.id],
      },
      wait: !currentProduct?.id,
    },
    [currentProduct?.id]
  );
  //#endregion

  //#region Data editing
  const currentProvinceTaxes = currentProduct?.productTaxes?.map((it) => it.provinceCode);
  const form = useForm<ProductForm>(
    {
      default: {
        ...currentProduct,
        productPricingAutomationRules: productPricingAutomationRuleListApi?.payload?.data ?? [],
        productTaxes: [
          ...(currentProduct?.productTaxes || []),
          ...difference(keys(CanadaProvinces), currentProvinceTaxes).map((provinceCode) => ({
            productId: currentProduct?.id || undefined,
            provinceCode,
            claimsProvincialSalesTax: 0,
            claimsGstHst: 0,
            dealerCostProvincialSalesTax: 0,
            dealerCostGstHst: 0,
          })),
        ],
      },
      onSubmit: async ({
        productDocuments,
        productRefundDocuments,
        productTransferOwnershipDocuments,
        productPricingAutomationRules,
        ...product
      }) => {
        const productSaveResponse = await productSaveApi({
          ...product,
          productDocumentTemplates: product.productDocumentTemplates?.map((g) => (g.id <= 0 ? omit(g, 'id') : g)),
          productProvinceGroups: product.productProvinceGroups?.map((g) => (g.id <= 0 ? omit(g, 'id') : g)),
        });

        if (productSaveResponse.status !== HttpStatus.ok) {
          addLog({ error: productSaveResponse.message });
          return;
        }

        const productId = productSaveResponse.payload;
        if (productDocuments?.length) {
          for (const pd of productDocuments) {
            await productDocumentUpdateApi(product.id || productId, pd.id, {
              fillablePdfFields: pd.fillablePdfFields,
              signatureFields: pd.signatureFields,
            });
          }
          await productDocumentOrderApi(
            productDocuments?.reduce(
              (p, c) => ({
                ...p,
                [c.id]: c.orderNumber ?? 0,
              }),
              {}
            )
          );
        }
        for (const doc of productDocuments ?? []) {
          if (doc.isFileReplaced && doc.formFile) {
            await productDocumentReplaceApi({
              productId: product.id || productId,
              documentId: doc.id,
              formFile: doc.formFile,
            });
          }
        }
        if (productRefundDocuments?.length) {
          for (const prd of productRefundDocuments) {
            await productRefundDocumentUpdateApi(product.id || productId, prd.id, {
              fillablePdfFields: prd.fillablePdfFields,
            });
          }
          await productRefundDocumentOrderApi(
            productRefundDocuments?.reduce(
              (p, c) => ({
                ...p,
                [c.id]: c.orderNumber ?? 0,
              }),
              {}
            )
          );
        }
        for (const doc of productRefundDocuments ?? []) {
          if (doc.isFileReplaced && doc.formFile) {
            await productRefundDocumentReplaceApi({
              productId: product.id || productId,
              documentId: doc.id,
              formFile: doc.formFile,
            });
          }
        }
        if (productTransferOwnershipDocuments?.length) {
          for (const ptod of productTransferOwnershipDocuments) {
            await productTransferOwnershipDocumentUpdateApi(product.id || productId, ptod.id, {
              fillablePdfFields: ptod.fillablePdfFields,
            });
          }
          await productTransferOwnershipDocumentOrderApi(
            productTransferOwnershipDocuments?.reduce(
              (p, c) => ({
                ...p,
                [c.id]: c.orderNumber ?? 0,
              }),
              {}
            )
          );
        }
        for (const doc of productTransferOwnershipDocuments ?? []) {
          if (doc.isFileReplaced && doc.formFile) {
            await productTransferOwnershipDocumentReplaceApi({
              productId: product.id || productId,
              documentId: doc.id,
              formFile: doc.formFile,
            });
          }
        }
        if (!isEmpty(productPricingAutomationRules ?? []) && productId) {
          for (const productPricingAutomationRule of productPricingAutomationRules ?? []) {
            if (productPricingAutomationRule.id && productPricingAutomationRule.isDeleted) {
              await productPricingAutomationRuleDeleteApi(productPricingAutomationRule.id);
            } else {
              await productPricingAutomationRuleSaveApi({ ...productPricingAutomationRule, productId });
            }
          }
        }

        addLog({ success: lang.saveSuccess });
        if (productId?.toString() === pageRouter.params.id) {
          reloadProduct({ id: productId });
          productPricingAutomationRuleListApi.execute({ productId: [productId] });
        } else {
          pageRouter.updateParams({
            action: ProductAction.EDIT,
            id: productId.toString(),
          });
        }
      },
      validation: (data, errors) => {
        if (!data.name) errors.name = lang.mustNotBeEmpty;
        if (!data.code) errors.code = lang.mustNotBeEmpty;
        if (!data.providerId) errors.providerId = lang.mustNotBeEmpty;
        if (!data.pricingDistributionTemplateId) errors.pricingDistributionTemplateId = lang.mustNotBeEmpty;
        if (!data.productType) errors.productType = lang.mustNotBeEmpty;
        if (!data.category) set(errors, 'category._objectError', lang.mustNotBeEmpty);
        if (!data.insurers || data.insurers.length === 0) set(errors, 'insurers._objectError', lang.mustNotBeEmpty);
        if (!data.productTaxes || data.productTaxes.length === 0)
          set(errors, 'productTaxes._objectError', lang.mustNotBeEmpty);
        if (data.productProvinceGroups && data.productProvinceGroups.some((g) => !g.provinceCodes?.length))
          set(errors, 'productProvinceGroups.name', lang.mustNotBeEmpty);
        if (data.productProvinceGroups && data.productProvinceGroups.some((g) => !g.name))
          set(errors, 'productProvinceGroups.provinceCodes', lang.mustNotBeEmpty);
        const pricingAutomationSchema = createPricingAutomationSchema({ subfees: data.subfee });
        if (
          data.productPricingAutomationRules?.some(
            (rule) =>
              hasIncompleteAutomationExpressions(rule as Model.IProductPricingAutomationRule) ||
              hasIncompleteAutomationEffects(rule as Model.IProductPricingAutomationRule, pricingAutomationSchema)
          )
        )
          set(errors, 'productPricingAutomationRules._objectError', lang.required);

        if (data.isCustomerConsentRequired) {
          if (!data.consentSenderEmail) errors.consentSenderEmail = lang.mustNotBeEmpty;
          if (
            data.consentSenderEmail &&
            !(
              isEmailDomain(data.consentSenderEmail, 'goautoshield.com') ||
              isEmailDomain(data.consentSenderEmail, 'asc-insurance.com')
            )
          )
            errors.consentSenderEmail = lang.mustBeEmailDomain.replace(
              '{domain}',
              'goautoshield.com or asc-insurance.com'
            );

          if (!data.consentVideoLink) errors.consentVideoLink = lang.mustNotBeEmpty;
        }
      },
    },
    [product, productPricingAutomationRuleListApi?.payload?.data]
  );
  //#endregion
  function reload() {
    reloadProduct((body) => body);
  }

  //#region Product Documents
  const newProductDocuments = form.data.productDocuments?.filter((d) => !d.id) || [];
  const uploadedProductDocuments = form.data.productDocuments?.filter((d) => !!d.id) || [];
  const newProductRefundDocuments = form.data.productRefundDocuments?.filter((d) => !d.id) || [];
  const uploadedProductRefundDocuments = form.data.productRefundDocuments?.filter((d) => !!d.id) || [];
  const newProductTransferOwnershipDocuments = form.data.productTransferOwnershipDocuments?.filter((d) => !d.id) || [];
  const uploadedProductTransferOwnershipDocuments =
    form.data.productTransferOwnershipDocuments?.filter((d) => !!d.id) || [];

  async function uploadProductDocumentFile(
    params: Pick<Model.IProductDocument, 'formFile' | 'documentCategory' | 'orderNumber'>
  ) {
    if (!params.formFile || !params.documentCategory) return;
    setIsLoading(true);
    await productDocumentSaveApi({
      ...params,
      productId: product.id,
      languageIsoCode: Language.english,
    });
    setIsLoading(false);
    reload();
    return;
  }
  async function deleteProductDocumentFile(documentId: number) {
    if (!documentId) return;
    setIsLoading(true);
    const translatedDocs = await documentTranslationListApi({
      documentId: [documentId],
      documentType: 'ProductDocument',
    });
    for (const translatedDoc of translatedDocs.payload?.data ?? []) {
      await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
    }
    await productDocumentDeleteApi({ productId: form.data?.id, documentId });
    setIsLoading(false);
    reload();
  }
  async function uploadProductRefundDocumentFile(
    params: Pick<Model.IProductRefundDocument, 'formFile' | 'documentCategory' | 'orderNumber'>
  ) {
    if (!params.formFile || !params.documentCategory) return;
    setIsLoading(true);
    await productRefundDocumentSaveApi({
      ...params,
      productId: product.id,
      languageIsoCode: Language.english,
    });
    setIsLoading(false);
    reload();
    return;
  }
  async function deleteProductRefundDocumentFile(documentId: number) {
    if (!documentId) return;
    setIsLoading(true);
    const translatedDocs = await documentTranslationListApi({
      documentId: [documentId],
      documentType: 'ProductRefundDocument',
    });
    for (const translatedDoc of translatedDocs.payload?.data ?? []) {
      await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
    }
    await productRefundDocumentDeleteApi({ productId: form.data?.id, documentId });
    setIsLoading(false);
    reload();
  }
  async function uploadProductTransferOwnershipDocumentFile(
    params: Pick<Model.IProductTransferOwnershipDocument, 'formFile' | 'documentCategory' | 'orderNumber'>
  ) {
    if (!params.formFile || !params.documentCategory) return;
    setIsLoading(true);
    await productTransferOwnershipDocumentSaveApi({
      ...params,
      productId: product.id,
      languageIsoCode: Language.english,
    });
    setIsLoading(false);
    reload();
    return;
  }
  async function deleteProductTransferOwnershipDocumentFile(documentId: number) {
    if (!documentId) return;
    setIsLoading(true);
    const translatedDocs = await documentTranslationListApi({
      documentId: [documentId],
      documentType: 'ProductTransferOwnershipDocument',
    });
    for (const translatedDoc of translatedDocs.payload?.data ?? []) {
      await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
    }
    await productTransferOwnershipDocumentDeleteApi({ productId: form.data?.id, documentId });
    setIsLoading(false);
    reload();
  }
  //#endregion

  return {
    reload,
    uploadProductDocumentFile,
    deleteProductDocumentFile,
    uploadProductRefundDocumentFile,
    deleteProductRefundDocumentFile,
    uploadProductTransferOwnershipDocumentFile,
    deleteProductTransferOwnershipDocumentFile,
    newProductDocuments,
    newProductRefundDocuments,
    newProductTransferOwnershipDocuments,
    uploadedProductDocuments,
    uploadedProductRefundDocuments,
    uploadedProductTransferOwnershipDocuments,
    product,
    pageRouter,
    listApi,
    deleteApi,
    cloneApi,
    productPricingAutomationRuleListApi,
    form,
    selected,
    permissions,
    isEditLoading: isEditLoading || isLoading,
    canView,
    views: {
      [ProductAction.DELETE]:
        pageRouter.params.action === ProductAction.DELETE && selected && permissions.DELETE_PRODUCT,
      [ProductAction.CLONE]: pageRouter.params.action === ProductAction.CLONE && selected && permissions.CREATE_PRODUCT,
      [ProductAction.EDIT]:
        pageRouter.params.action === ProductAction.EDIT &&
        ((permissions.CREATE_PRODUCT && !selected) || (permissions.EDIT_PRODUCT && selected)),
    },
  };
}

export function useProductProvider() {
  return useDataProvider<ReturnType<typeof useProduct>>();
}
